import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';

const AIInsightsNew = () => {
  const { user, authFetch } = useAuth();
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('business');
  
  // Load persisted data from localStorage on mount
  const loadPersistedData = () => {
    try {
      const savedInsights = localStorage.getItem('bissa_ai_insights');
      const savedLeadScores = localStorage.getItem('bissa_lead_scores');
      const savedContactHealth = localStorage.getItem('bissa_contact_health');
      
      return {
        insights: savedInsights ? JSON.parse(savedInsights) : null,
        leadScores: savedLeadScores ? JSON.parse(savedLeadScores) : null,
        contactHealth: savedContactHealth ? JSON.parse(savedContactHealth) : null
      };
    } catch (err) {
      console.error('Error loading persisted data:', err);
      return { insights: null, leadScores: null, contactHealth: null };
    }
  };
  
  const persistedData = loadPersistedData();
  const [insights, setInsights] = useState(persistedData.insights);
  const [leadScores, setLeadScores] = useState(persistedData.leadScores);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [error, setError] = useState(null);
  
  // Save insights to localStorage whenever they change
  useEffect(() => {
    if (insights) {
      try {
        localStorage.setItem('bissa_ai_insights', JSON.stringify(insights));
        // Store timestamp to know when it was generated
        localStorage.setItem('bissa_ai_insights_timestamp', new Date().toISOString());
      } catch (err) {
        console.error('Error saving insights:', err);
      }
    }
  }, [insights]);
  
  // Save lead scores to localStorage whenever they change
  useEffect(() => {
    if (leadScores) {
      try {
        localStorage.setItem('bissa_lead_scores', JSON.stringify(leadScores));
        localStorage.setItem('bissa_lead_scores_timestamp', new Date().toISOString());
      } catch (err) {
        console.error('Error saving lead scores:', err);
      }
    }
  }, [leadScores]);

  const generateBusinessInsights = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    // Only clear insights if forcing a refresh
    if (forceRefresh) {
      setInsights(null);
      localStorage.removeItem('bissa_ai_insights');
      localStorage.removeItem('bissa_ai_insights_timestamp');
    }

    try {
      console.log('🚀 NEW AI INSIGHTS v2.0 - Generating business insights...');
      
      // Call the backend API to get AI insights (with cache busting)
      const timestamp = new Date().getTime();
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/daily-insights?v=${timestamp}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch insights: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('AI insights received:', data);
      setInsights(data);
      
    } catch (err) {
      console.error('Error fetching AI insights:', err);
      setError(err.message || 'Failed to generate AI insights');
    } finally {
      setLoading(false);
    }
  };

  const generateLeadScoring = async (selectedContactIds = []) => {
    if (selectedContactIds.length === 0) {
      setError('Please select at least one contact to score');
      return;
    }
    if (selectedContactIds.length > 5) {
      setError('Maximum 5 contacts can be scored at a time');
      return;
    }

    setLoadingLeads(true);
    setError(null);
    setLeadScores(null);

    try {
      console.log('🎯 Generating lead scoring for selected contacts...', selectedContactIds);
      
      // Call new lead scoring endpoint with selected contact IDs
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/lead-scoring`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          contact_ids: selectedContactIds
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Lead scoring error response:', errorText);
        throw new Error(`Failed to fetch lead scores: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Lead scores received:', data);
      // Transform response to match expected format
      const transformedData = {
        total_processed: data.total_processed,
        top_leads: data.scores,
        all_scores: data.scores,
        processed_at: data.processed_at
      };
      setLeadScores(transformedData);
      
    } catch (err) {
      console.error('Error fetching lead scores:', err);
      // Filter out browser extension errors
      if (err.message && err.message.includes('message channel closed')) {
        console.warn('Browser extension error detected, ignoring...');
        setError('Please disable browser extensions and try again, or check the console for details.');
      } else if (err.message && err.message.includes('CORS')) {
        setError('CORS error: Please check backend CORS configuration');
      } else {
        setError(err.message || 'Failed to generate lead scores');
      }
    } finally {
      setLoadingLeads(false);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/contacts`);
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
    }
  };

  const getContactHealth = async (contactId) => {
    try {
      const response = await authFetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/contact-health/${contactId}`);
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      console.error('Error fetching contact health:', err);
    }
    return null;
  };

  // Fetch contacts when component mounts or user changes
  useEffect(() => {
    if (user) {
      fetchContacts();
    }
  }, [user]);

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <div className="text-6xl mb-4">🧠</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">AI Insights</h1>
          <p className="text-gray-600 mb-6">Please log in to access AI-powered insights about your business</p>
          <button 
            onClick={() => window.location.href = '/'}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 sm:mb-8 pb-4 sm:pb-6 border-b border-gray-200">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2 sm:gap-3">
            🧠 AI Insights
          </h1>
          <p className="text-sm sm:text-base text-gray-600 px-2 sm:px-0">
            Get AI-powered insights about your business, leads, and contact relationships
          </p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex gap-2">
          <button 
            onClick={() => {
              if (activeTab === 'business') generateBusinessInsights();
              // Lead scoring now handled by the tab component itself
            }}
            disabled={loading || loadingLeads}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold text-sm sm:text-base transition-all duration-200 transform hover:scale-105 disabled:transform-none shadow-lg"
          >
            {(loading || loadingLeads) ? (
              <>
                <span className="inline-block animate-spin mr-1 sm:mr-2">⏳</span>
                <span className="hidden sm:inline">Analyzing...</span>
                <span className="sm:hidden">...</span>
              </>
            ) : (
              <>
                🚀 <span className="hidden sm:inline">Get Insights</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex bg-gray-100 p-1 rounded-lg mb-4 sm:mb-6 overflow-x-auto">
        <button
          onClick={() => setActiveTab('business')}
          className={`flex-1 py-2 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'business'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          📊 <span className="hidden sm:inline">Business Insights</span>
        </button>
        <button
          onClick={() => setActiveTab('leads')}
          className={`flex-1 py-2 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'leads'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          🎯 <span className="hidden sm:inline">Lead Scoring</span>
        </button>
        <button
          onClick={() => setActiveTab('health')}
          className={`flex-1 py-2 px-2 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
            activeTab === 'health'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          ❤️ <span className="hidden sm:inline">Contact Health</span>
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <div className="flex items-center mb-3">
            <span className="text-2xl mr-3">❌</span>
            <h3 className="text-lg font-semibold text-red-800">Error</h3>
          </div>
          <p className="text-red-700 mb-4">{error}</p>
          <button 
            onClick={() => {
              setError(null);
              if (activeTab === 'business') generateBusinessInsights();
              else if (activeTab === 'leads') generateLeadScoring();
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'business' && (
        <BusinessInsightsTab 
          insights={insights}
          loading={loading}
          onRefresh={(force) => generateBusinessInsights(force)}
          user={user}
          contacts={contacts}
          t={t}
        />
      )}

      {activeTab === 'leads' && (
        <LeadScoringTab 
          leadScores={leadScores}
          loading={loadingLeads}
          onRefresh={generateLeadScoring}
          contacts={contacts}
          t={t}
          error={error}
        />
      )}

      {activeTab === 'health' && (
        <ContactHealthTab 
          contacts={contacts}
          getContactHealth={getContactHealth}
          t={t}
        />
      )}
    </div>
  );
};

// Business Insights Tab Component
const BusinessInsightsTab = ({ insights, loading, onRefresh, user, contacts, t }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="animate-spin text-6xl mb-4">🧠</div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">AI Analyzing Your Data...</h2>
        <p className="text-gray-600">This may take a few moments while our AI reviews your CRM data</p>
      </div>
    );
  }

  if (!insights) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 text-center border border-gray-200">
        <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">
          🧠
        </div>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          Welcome to AI Insights, {user?.name || 'there'}!
        </h2>
        
        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
          Get AI-powered insights about your {contacts.length} contacts, deals, and business performance. Click below to generate your first analysis.
        </p>
        
        <button 
          onClick={onRefresh}
          className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-semibold text-lg flex items-center gap-2 mx-auto transition-all duration-200 transform hover:scale-105 shadow-lg"
        >
          🚀 Get Insights
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Business Summary */}
      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <div className="flex items-center mb-4">
          <span className="text-3xl mr-3">📊</span>
          <h2 className="text-2xl font-bold text-gray-900">Business Insights Summary</h2>
        </div>
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
          <p className="text-gray-800 text-lg leading-relaxed">{insights.summary}</p>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          Generated: {new Date(insights.generated_at).toLocaleString()}
        </div>
      </div>

      {/* Key Insights */}
      {insights.insights && insights.insights.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
          <div className="flex items-center mb-4">
            <span className="text-3xl mr-3">💡</span>
            <h2 className="text-2xl font-bold text-gray-900">Key Insights</h2>
          </div>
          <div className="space-y-3">
            {insights.insights.map((insight, index) => (
              <div key={index} className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                <p className="text-gray-800">{insight}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Opportunities */}
      {insights.opportunities && insights.opportunities.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
          <div className="flex items-center mb-4">
            <span className="text-3xl mr-3">🎯</span>
            <h2 className="text-2xl font-bold text-gray-900">Growth Opportunities</h2>
          </div>
          <div className="space-y-3">
            {insights.opportunities.map((opportunity, index) => (
              <div key={index} className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
                <p className="text-gray-800">{opportunity}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Alerts */}
      {insights.alerts && insights.alerts.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
          <div className="flex items-center mb-4">
            <span className="text-3xl mr-3">⚠️</span>
            <h2 className="text-2xl font-bold text-gray-900">Important Alerts</h2>
          </div>
          <div className="space-y-3">
            {insights.alerts.map((alert, index) => (
              <div key={index} className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                <p className="text-gray-800">{alert}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {insights.recommendations && insights.recommendations.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
          <div className="flex items-center mb-4">
            <span className="text-3xl mr-3">📋</span>
            <h2 className="text-2xl font-bold text-gray-900">AI Recommendations</h2>
          </div>
          <div className="space-y-3">
            {insights.recommendations.map((recommendation, index) => (
              <div key={index} className="bg-purple-50 border-l-4 border-purple-400 p-4 rounded flex items-start">
                <span className="text-purple-600 font-bold mr-3">{index + 1}.</span>
                <p className="text-gray-800">{recommendation}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action CTA */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg shadow-lg p-6 text-white text-center">
        <h3 className="text-xl font-bold mb-2">Ready to Act on These Insights?</h3>
        <p className="mb-4 opacity-90">Take action on your AI recommendations to accelerate business growth</p>
        <div className="flex flex-wrap justify-center gap-3">
          <button 
            onClick={() => window.location.href = '/contacts'}
            className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            View Contacts
          </button>
          <button 
            onClick={() => window.location.href = '/deals'}
            className="bg-white text-blue-600 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Manage Deals
          </button>
          <button 
            onClick={() => onRefresh(true)}
            className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg font-semibold hover:bg-opacity-30 transition-colors"
          >
            Refresh Analysis
          </button>
        </div>
      </div>
    </div>
  );
};

// Lead Scoring Tab Component
const LeadScoringTab = ({ leadScores, loading, onRefresh, contacts, t }) => {
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [error, setError] = useState(null);

  // Auto-select first contact if none selected and contacts are available
  useEffect(() => {
    if (contacts.length > 0 && selectedContacts.length === 0) {
      setSelectedContacts([contacts[0].id]);
    }
  }, [contacts]);

  const handleScoreLeads = () => {
    if (selectedContacts.length === 0) {
      setError('Please select at least one contact to score');
      return;
    }
    if (selectedContacts.length > 5) {
      setError('Maximum 5 contacts can be scored at a time');
      return;
    }
    setError(null);
    onRefresh(selectedContacts);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="animate-spin text-6xl mb-4">🎯</div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">AI Scoring Your Leads...</h2>
        <p className="text-gray-600">Analyzing engagement patterns and conversion potential</p>
      </div>
    );
  }

  if (!leadScores) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <span className="text-3xl mr-3">🎯</span>
              <h2 className="text-2xl font-bold text-gray-900">AI Lead Scoring</h2>
            </div>
            <button 
              onClick={handleScoreLeads}
              disabled={loading || selectedContacts.length === 0 || contacts.length === 0}
              className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-semibold transition-colors"
              title={contacts.length === 0 ? 'No contacts available' : selectedContacts.length === 0 ? 'Please select at least one contact' : ''}
            >
              🎯 Score Selected Leads ({selectedContacts.length})
            </button>
          </div>
          
          <p className="text-gray-600 mb-4">
            Select up to 5 contacts to score based on engagement patterns, conversion probability, and business value.
          </p>
          
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
          
          {/* Contact Selection */}
          <div className="border-t pt-4">
            <h3 className="font-medium text-gray-900 mb-2">Select Contacts to Score (1-5 max):</h3>
            {contacts.length === 0 ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm">No contacts available. Please add contacts first.</p>
              </div>
            ) : (
              <>
                <div className="max-h-60 overflow-y-auto">
                  <div className="space-y-2">
                    {contacts.map(contact => (
                      <label key={contact.id} className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded">
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              if (selectedContacts.length >= 5) {
                                setError('Maximum 5 contacts can be selected at a time');
                                return;
                              }
                              setSelectedContacts([...selectedContacts, contact.id]);
                              setError(null);
                            } else {
                              setSelectedContacts(selectedContacts.filter(id => id !== contact.id));
                              setError(null);
                            }
                          }}
                          className="mr-2 cursor-pointer"
                        />
                        <span className="text-sm text-gray-700">{contact.name} ({contact.email || 'No email'})</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  {selectedContacts.length} selected (max 5 can be scored at a time)
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'hot': return 'bg-red-100 text-red-800 border-red-200';
      case 'warm': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-red-600';
    if (score >= 60) return 'text-orange-600';
    return 'text-gray-600';
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <span className="text-3xl mr-3">🎯</span>
            <h2 className="text-2xl font-bold text-gray-900">Lead Scoring Results</h2>
          </div>
          <button 
            onClick={onRefresh}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
          >
            🔄 Refresh Scores
          </button>
        </div>
        
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded mb-4">
          <div className="flex flex-wrap gap-4 text-sm">
            <span><strong>{leadScores.total_processed}</strong> contacts analyzed</span>
            <span><strong>{leadScores.top_leads?.length || 0}</strong> top priority leads</span>
            <span>Generated: {new Date(leadScores.processed_at).toLocaleString()}</span>
          </div>
        </div>

        {/* Top Leads */}
        {leadScores.top_leads && leadScores.top_leads.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">🔥 Top Priority Leads</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {leadScores.top_leads.slice(0, 6).map((lead, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-semibold text-gray-900 truncate">{lead.contact_name}</h4>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(lead.priority)}`}>
                      {lead.priority.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`text-2xl font-bold ${getScoreColor(lead.score)}`}>{lead.score}</span>
                    <span className="text-gray-400">/100</span>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <div 
                      className={`h-2 rounded-full ${
                        lead.score >= 80 ? 'bg-red-500' : 
                        lead.score >= 60 ? 'bg-orange-500' : 'bg-gray-500'
                      }`}
                      style={{ width: `${lead.score}%` }}
                    ></div>
                  </div>
                  
                  {/* Key factors */}
                  {lead.key_factors && lead.key_factors.length > 0 && (
                    <div className="space-y-1">
                      {lead.key_factors.slice(0, 2).map((factor, idx) => (
                        <div key={idx} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">
                          {factor}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* All Scores Table */}
      {leadScores.all_scores && leadScores.all_scores.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 All Lead Scores</h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-4 font-medium text-gray-700">Contact</th>
                  <th className="text-center py-2 px-4 font-medium text-gray-700">Score</th>
                  <th className="text-center py-2 px-4 font-medium text-gray-700">Priority</th>
                  <th className="text-left py-2 px-4 font-medium text-gray-700">Key Factors</th>
                </tr>
              </thead>
              <tbody>
                {leadScores.all_scores.slice(0, 20).map((lead, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{lead.contact_name}</div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`text-xl font-bold ${getScoreColor(lead.score)}`}>
                        {lead.score}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(lead.priority)}`}>
                        {lead.priority.toUpperCase()}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {lead.key_factors?.slice(0, 2).map((factor, idx) => (
                          <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                            {factor}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {leadScores.all_scores.length > 20 && (
            <div className="mt-4 text-center text-gray-500">
              Showing top 20 of {leadScores.all_scores.length} scored contacts
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Contact Health Tab Component
const ContactHealthTab = ({ contacts, getContactHealth, t }) => {
  const [healthData, setHealthData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [error, setError] = useState(null);

  // Auto-select first contact if none selected and contacts are available
  useEffect(() => {
    if (contacts.length > 0 && selectedContacts.length === 0) {
      setSelectedContacts([contacts[0].id]);
    }
  }, [contacts]);

  const analyzeContactHealth = async () => {
    if (selectedContacts.length === 0) {
      setError('Please select at least one contact to analyze');
      return;
    }

    setLoading(true);
    setError(null);
    const newHealthData = {};
    
    try {
      for (const contactId of selectedContacts.slice(0, 10)) { // Limit to 10 for performance
        try {
          const health = await getContactHealth(contactId);
          if (health) {
            newHealthData[contactId] = health;
          }
        } catch (err) {
          console.error(`Error analyzing health for contact ${contactId}:`, err);
          // Filter out browser extension errors
          if (err.message && !err.message.includes('message channel closed')) {
            setError(`Error analyzing contact: ${err.message}`);
          }
        }
      }
      
      if (Object.keys(newHealthData).length === 0) {
        setError('No health data could be retrieved. Please try again.');
      } else {
        setHealthData(newHealthData);
      }
    } catch (err) {
      console.error('Error in analyzeContactHealth:', err);
      setError(err.message || 'Failed to analyze contact health');
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getRiskColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const hasHealthData = Object.keys(healthData).length > 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <span className="text-3xl mr-3">❤️</span>
            <h2 className="text-2xl font-bold text-gray-900">Contact Health Analysis</h2>
          </div>
          <button 
            onClick={analyzeContactHealth}
            disabled={loading || selectedContacts.length === 0 || contacts.length === 0}
            className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-semibold transition-colors"
            title={contacts.length === 0 ? 'No contacts available' : selectedContacts.length === 0 ? 'Please select at least one contact' : ''}
          >
            {loading ? '⏳ Analyzing...' : '❤️ Analyze Health'}
          </button>
        </div>
        
        <p className="text-gray-600 mb-4">
          Monitor relationship strength, engagement trends, and identify contacts at risk.
        </p>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}
        
        {/* Contact Selection */}
        <div className="border-t pt-4">
          <h3 className="font-medium text-gray-900 mb-2">Select Contacts to Analyze:</h3>
          {contacts.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-yellow-800 text-sm">No contacts available. Please add contacts first.</p>
            </div>
          ) : (
            <>
              <div className="max-h-40 overflow-y-auto">
                <div className="space-y-2">
                  {contacts.slice(0, 15).map(contact => (
                    <label key={contact.id} className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedContacts.includes(contact.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedContacts([...selectedContacts, contact.id]);
                          } else {
                            setSelectedContacts(selectedContacts.filter(id => id !== contact.id));
                          }
                        }}
                        className="mr-2 cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{contact.name} ({contact.email || 'No email'})</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {selectedContacts.length} selected (max 10 will be analyzed)
              </div>
            </>
          )}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="animate-spin text-6xl mb-4">❤️</div>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">Analyzing Contact Health...</h2>
          <p className="text-gray-600">Evaluating relationship strength and engagement patterns</p>
        </div>
      )}

      {/* Health Results */}
      {hasHealthData && !loading && (
        <div className="space-y-6">
          {Object.entries(healthData).map(([contactId, health]) => {
            const contact = contacts.find(c => c.id === contactId);
            if (!contact || !health) return null;

            return (
              <div key={contactId} className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center">
                    <h3 className="text-lg font-semibold text-gray-900">{contact.name}</h3>
                    <span className="ml-2 text-sm text-gray-500">({contact.email})</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className={`text-3xl font-bold ${getHealthColor(health.health_score)}`}>
                        {health.health_score}
                      </div>
                      <div className="text-xs text-gray-500">Health Score</div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getRiskColor(health.risk_level)}`}>
                      {health.risk_level?.toUpperCase() || 'UNKNOWN'} RISK
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Trend */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">📈 Trend</h4>
                    <p className="text-blue-800 capitalize">{health.trend || 'Stable'}</p>
                  </div>

                  {/* Risk Factors */}
                  {health.risk_factors && health.risk_factors.length > 0 && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h4 className="font-medium text-yellow-900 mb-2">⚠️ Risk Factors</h4>
                      <ul className="text-sm text-yellow-800 space-y-1">
                        {health.risk_factors.slice(0, 2).map((factor, idx) => (
                          <li key={idx}>• {factor}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Suggestions */}
                  {health.suggestions && health.suggestions.length > 0 && (
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-medium text-green-900 mb-2">💡 Suggestions</h4>
                      <ul className="text-sm text-green-800 space-y-1">
                        {health.suggestions.slice(0, 2).map((suggestion, idx) => (
                          <li key={idx}>• {suggestion}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Key Insights */}
                {health.key_insights && health.key_insights.length > 0 && (
                  <div className="mt-4 bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-medium text-purple-900 mb-2">🔍 Key Insights</h4>
                    <div className="space-y-1">
                      {health.key_insights.map((insight, idx) => (
                        <p key={idx} className="text-sm text-purple-800">• {insight}</p>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 text-xs text-gray-500">
                  Analyzed: {new Date(health.analyzed_at).toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!hasHealthData && !loading && (
        <div className="bg-white rounded-lg shadow-lg p-8 text-center border border-gray-200">
          <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">
            ❤️
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Contact Health Analysis
          </h2>
          
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Select contacts above and click "Analyze Health" to get AI-powered insights about relationship strength, engagement trends, and risk factors.
          </p>
        </div>
      )}
    </div>
  );
};

export default AIInsightsNew;